import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/driving-license — EN hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { DL_NATIONALITIES, DL_REGION_LABELS_EN, type DlRegion } from "@/data/driving-license-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Car, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, MapPin, Clock, Wallet } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/driving-license/")({
  head: () => {
    const url = `${SITE}/en/driving-license`;
    const title = "Convert Foreign Driving License to Thai DLT 5-Year License | NYC";
    const desc = "Convert your foreign driving license to a Thai 5-year DLT license. Get your timeline and checklist in minutes — chat now.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/driving-license", "en") }, { name: "description", content: ctaDesc(desc, "/en/driving-license", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/driving-license", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/driving-license", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "convert driving license Thailand, Thai DLT, foreign driver license, translate driving license Thai, Geneva 1949 Thailand" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks("/driving-license", "/en/driving-license"),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en",
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: DL_NATIONALITIES.length,
                itemListElement: DL_NATIONALITIES.map((n, i) => ({
                  "@type": "ListItem", position: i + 1, name: `Convert ${n.demonymEn} driving license to Thai`,
                  url: `${SITE}/en/driving-license/${n.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Foreign Driving License to Thai DLT Conversion",
              serviceType: "Foreign Driving License to Thai DLT Conversion",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "4500", priceCurrency: "THB", description: "Translate + Embassy verify + DLT submission" },
            }),
          },
          howToSchema({
            name: "How to convert a foreign driving license to a Thai 5-year license",
            description: "5-step process for converting a foreign driver license at Thailand DLT",
            totalTime: "P3D",
            estimatedCost: { currency: "THB", value: "4500" },
            inLanguage: "en",
            steps: [
              { name: "Prepare documents", text: "Original license + passport + visa/work permit + Certificate of Residence + medical certificate (5 conditions)" },
              { name: "Translate license", text: "Source language → Thai by MOJ-registered translator, THB 800-1,500", url: `${SITE}/en/translate` },
              { name: "Embassy verification", text: "Bring license + translation to your home-country embassy in Bangkok for affidavit/notarial verification" },
              { name: "Submit at DLT", text: "Department of Land Transport (Chatuchak/Bangphli/Samut Prakan) — vision + reaction tests + (China/India: written exam)" },
              { name: "Receive Thai 5-year license", text: "THB 605 — smart-card plastic license issued same day" },
            ],
          }),
          faqPageSchema([
            { q: "How long does the conversion take?", a: "1–2 Thai business days for English-language licences (UK, Australia, Singapore, USA) that require no translation, 3–4 days for European licences needing certified MoJ translation plus embassy verification, and 5–7 days for China and other non-Hague countries that need full 2-tier legalisation (embassy + MFA). NYC books your DLT slot in advance to avoid walk-in queues." },
            { q: "Do I need to take a driving test?", a: "No practical driving test if your original licence is from a Geneva 1949 or Vienna 1968 Convention signatory (over 100 jurisdictions) — DLT grants direct conversion under Ministerial Regulation B.E.2555. Only Mainland Chinese licences require a written theory test in Thai or English; NYC provides the 50-question DLT study guide and on-site interpreter." },
            { q: "Is an International Driving Permit (IDP) enough?", a: "An IDP allows temporary driving for up to 1 year on tourist or short-stay status. If you live in Thailand on a Non-B, LTR, DTV, Marriage, Retirement or work visa, the Department of Land Transport requires a Thai 5-year licence (renewable for 5 more) under Land Traffic Act §42. Insurance claims on an IDP-only basis are often partially voided after 12 months." },
            { q: "Can a Thai licence be used abroad?", a: "Yes. A Thai 5-year licence paired with a DLT-issued IDP (in either Geneva 1949 or Vienna 1968 format) is valid for short-term driving in 100+ signatory countries, including all EU/EEA states, UK, Australia, Japan, South Korea and most ASEAN. NYC obtains the IDP for THB 1,200 same-day at DLT Chatuchak." },
            { q: "What's the total cost?", a: "NYC end-to-end service THB 4,500 covers certified translation by a Lawyers Council-registered translator, embassy verification escort, and DLT escort with appointment booking. Add THB 605 DLT government fee (eye/colour test + 5-year card) plus embassy verification fees of THB 500–2,000 depending on country. All charges itemised on a Revenue Department e-Tax Invoice." },
          ]),
        ]),
      ],
    };
  },
  component: EnDrivingLicenseHub,
});

function EnDrivingLicenseHub() {
  const byRegion = DL_NATIONALITIES.reduce<Record<string, typeof DL_NATIONALITIES>>((acc, n) => {
    (acc[n.region] ||= []).push(n); return acc;
  }, {});

  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="driving-license" locale="en" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span>
            <span>Driving License</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Car className="mr-1 inline h-3 w-3" /> Driving License Conversion · Thai DLT
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Convert Foreign Driving License → Thai 5-Year License
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Covering {DL_NATIONALITIES.length} major nationalities · Geneva 1949 + Vienna 1968 · Translate + embassy verify + DLT escort — 1-7 working days
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How long does it take and how much does it cost to convert a foreign driving license to a Thai license?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              1-2 days (UK/Australia/Singapore — no translation needed) to 5-7 days (China — 2-tier legalization). DLT fee THB 605 + NYC service THB 4,500 (translation + embassy escort + DLT escort). No driving test required for Geneva 1949/Vienna 1968 signatories — NYC has processed 8,000+ cases.
            </p>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><Clock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Processing time</div><div className="font-bold">1-7 days</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Service from</div><div className="font-bold">THB 4,500</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><MapPin className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">DLT branches</div><div className="font-bold">77 provinces</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Choose your home country</h2>
          <p className="mt-2 text-sm text-muted-foreground">Grouped by region · Click to see Geneva/Vienna status, embassy details and timing</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(byRegion) as DlRegion[]).map((region) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary"><MapPin className="mr-1 inline h-4 w-4" />{DL_REGION_LABELS_EN[region]}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {byRegion[region].map((n) => (
                    <Link
                      key={n.slug}
                      to="/en/driving-license/$nationality"
                      params={{ nationality: n.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 text-2xl">{n.flag} <span className="text-base font-bold">{n.countryEn}</span></div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        {n.directConversion ? "✅ Direct" : "📝 Written test"} · DLT {n.dltDays}d
                      </div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                        {n.conventionStatus === "geneva_1949" && "Geneva 1949"}
                        {n.conventionStatus === "vienna_1968" && "Vienna 1968"}
                        {n.conventionStatus === "both" && "Geneva + Vienna"}
                        {n.conventionStatus === "none" && "Bilateral reciprocity"}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        View details <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Document translation", d: "ISO 17100 · 12 languages", to: "/en/translate" as const },
              { t: "Notary Public Attorney", d: "POA, Affidavit", to: "/en/notary" as const },
              { t: "Apostille (Hague)", d: "60+ member states", to: "/en/apostille" as const },
              { t: "MFA + Embassy Legalisation", d: "16 non-Hague countries", to: "/en/mfa-legalisation" as const },
              { t: "Work Permit + Non-B", d: "15 nationalities BOI fast-track", to: "/en/work-permit" as const },
              { t: "Visa services", d: "Non-B / LTR / DTV / Retirement", to: "/en/visa" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
